/**
 * The languages Athena is published in.
 *
 * Spanish first, and not alphabetically: it is the default, and the order of
 * this array is the order the language selector offers them in.
 *
 * The values are the URL prefixes. `/es/practices` and `/en/practices` are the
 * canonical routes (V1.md section 2), so a language is a two-letter tag rather
 * than a BCP-47 locale — there is one Spanish and one English in V1, and a
 * URL that said `es-CR` would be committing the product to a distinction it
 * does not make.
 *
 * Adding a third language is one entry here, one dictionary file, and nothing
 * else: every consumer reads the list rather than naming the two.
 */
export const LANGUAGES = ['es', 'en'];
/**
 * Spanish. The language a request gets when nothing better is known: no URL
 * prefix and no signed-in user, or a new account that has not chosen.
 *
 * Deliberately not the browser's `Accept-Language`. V1.md section 14 asks for
 * a fixed default, and a browser-derived one would make the same URL render
 * differently for two people, which is exactly what the URL prefix exists to
 * prevent.
 */
export const DEFAULT_LANGUAGE = 'es';
export function isLanguage(value) {
    return typeof value === 'string' && LANGUAGES.includes(value);
}
/** The language, or the default. For a URL segment or a stored value that may be anything. */
export function toLanguage(value) {
    return isLanguage(value) ? value : DEFAULT_LANGUAGE;
}
/**
 * Which catalog locale a language reads.
 *
 * Subject names are catalog rows keyed by BCP-47 tag (see the Prisma schema),
 * and the seed ships `en-US` and `es-CR`. This is the one place the product's
 * two-letter language meets those tags, so widening to a second Spanish market
 * is a change here rather than a change everywhere a subject is rendered.
 */
export const CATALOG_LOCALES = {
    es: 'es-CR',
    en: 'en-US',
};
/**
 * The same two languages, spelled the way the database and the GraphQL schema
 * spell them.
 *
 * Two representations, because they answer to different constraints. `es` is a
 * URL segment and a filename, and lower case is what a URL should be. `ES` is
 * a Postgres enum value and a GraphQL enum value, and both of those are
 * conventionally upper case in this codebase (see `AttemptStatus`,
 * `VersionStatus`). Mapping between them in one file is cheaper than either
 * convention losing.
 */
export const LANGUAGE_CODES = ['ES', 'EN'];
export const DEFAULT_LANGUAGE_CODE = 'ES';
export function isLanguageCode(value) {
    return typeof value === 'string' && LANGUAGE_CODES.includes(value);
}
export function toLanguageCode(language) {
    return language.toUpperCase();
}
/** The stored code as a language tag, or the default for anything unrecognised. */
export function fromLanguageCode(code) {
    return isLanguageCode(code) ? code.toLowerCase() : DEFAULT_LANGUAGE;
}
//# sourceMappingURL=language.js.map